import React, { useCallback, useEffect, useContext, useRef, useState } from 'react';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import { Alert, Box, SpaceBetween, StatusIndicator, Link, Spinner, Button, ColumnLayout, Container } from '@amzn/awsui-components-react';
import { GlobalAppContext } from '../App';
import { GLOBAL_ACTIONS } from 'src/services/global/GlobalReducer';
import DynamicBreadcrumb from 'src/models/navigation/DynamicBreadcrumb';
import {
    STATUS_INDICATOR_CONFIG,
    getExecutionStatusConfig,
} from 'src/components/multidimensional-allocation/common/Constants';
import { WorksheetStatus } from 'src/models/multidimensional-allocation/MultiAllocationSheetData';
import { ExecutionStatus } from 'src/models/multidimensional-allocation/ExecutionStatus';
import { MultidimensionalAllocationContext } from 'src/services/multidimensional-allocation/MultidimensionalAllocationContext';
import { ACTIONS } from 'src/services/multidimensional-allocation/MultidimensionalAllocationReducer';
import { multidimensionalAllocationReducer } from 'src/services/multidimensional-allocation/MultidimensionalAllocationReducer';
import { initialState } from 'src/services/multidimensional-allocation/MultidimensionalAllocationState';
import useReducerWithLogger from 'src/services/utils/ReducerWithLogger';
import MultidimensionalAllocationDataSourcesContainer from 'src/components/multidimensional-allocation/data-sources/MultidimensionalAllocationDataSourcesContainer';
import CloneWorksheetModal, {
    CloneWorksheetRequest,
} from 'src/components/multidimensional-allocation/CloneWorksheetModal';
import WorksheetExecutionGroupSelector from 'src/components/multidimensional-allocation/WorksheetExecutionGroupSelector';
import { TPELoadingSpinner } from 'src/components/shared/TPELoadingSpinner';
import { LinkedEntitiesModal } from 'src/components/multidimensional-allocation/shared/LinkedEntitiesModal';
import { EntityType } from 'src/models/multidimensional-allocation/EntityType';
import { WorksheetPastExecutionResultsComponent } from './WorksheetPastExecutionResultsComponent';
import { EditLockButtonBar, LockBanner, useLockActions, deriveViewModeFromLockInfo, LOCK_COMPONENT_TYPES, isBlockedByEditLock } from 'src/components/shared/edit-locking';
import { getFeatureFlags, isFeatureEnabledForUser, EDIT_LOCKING_FLAG } from 'src/services/FeatureFlagsService';
import { PromoteButton } from 'src/components/shared/promotion';
import { PromotionComponentType } from 'src/models/promotion/PromotionTypes';
import { getPermissions } from 'src/components/AppPermissions';
import { AppModules } from 'src/models/permissions/RolePermissions';
import CONSTANTS from 'src/utils/constants';
import { executionErrorOf } from 'src/components/multidimensional-allocation/hooks/executionTotal';

interface RouteParams {
    worksheetId: string;
}

/**
 * Component for displaying multidimensional allocation calculation worksheet
 * Displays the creation new calculation interface with Step 1 configuration section
 */
export default function MultidimensionalAllocationCalculationView() {
    const { worksheetId } = useParams<RouteParams>();
    const { pathname } = useLocation();
    const history = useHistory();
    const { globalDispatch, services, userProfile } = useContext(GlobalAppContext);
    const currentUser = userProfile?.user || '';
    const { canEdit } = getPermissions(AppModules.MULTIDIMENSIONAL_ALLOCATION);

    // Set up local state and dispatch
    const [multidimensionalAllocationState, multidimensionalAllocationDispatch] = useReducerWithLogger(
        multidimensionalAllocationReducer,
        initialState
    );

    // State for execute worksheet functionality
    const [executeRequest, setExecuteRequest] = useState<string | undefined>(undefined);
    const [retryRequest, setRetryRequest] = useState<string | undefined>(undefined);
    // A result link expires five minutes after the status call, so each past-view data-source open asks for new ones.
    const [resultLinkRefreshCount, setResultLinkRefreshCount] = useState(0);
    const refreshResultLinks = useCallback(() => setResultLinkRefreshCount(count => count + 1), []);

    const [isDisableExecuteButton, setIsDisableExecuteButton] = useState<boolean>(true);

    // State for clone worksheet modal
    const [isCloneModalVisible, setIsCloneModalVisible] = useState<boolean>(false);
    const [cloneRequest, setCloneRequest] = useState<CloneWorksheetRequest | null>(null);

    // State for linked entities modal
    const [isLinkedEntitiesModalVisible, setIsLinkedEntitiesModalVisible] = useState<boolean>(false);
    const [linkedEntitiesLoadTrigger, setLinkedEntitiesLoadTrigger] = useState<string>('');

    // State for execution group functionality
    const [loadTime, setLoadTime] = useState<string>('');
    const [saveExecutionGroupPayload, setSaveExecutionGroupPayload] = useState<any>(null);

    // Get selectedWorksheet from context
    const { selectedWorksheet, worksheetExecution } = multidimensionalAllocationState;

    // Check if viewing historical data
    const isViewingHistoricalData = worksheetExecution != null;
    const historicalWorksheetVersion = worksheetExecution?.worksheetVersion;

    // Edit-locking feature
    const [editLockingEnabled, setEditLockingEnabled] = useState<boolean>(false);
    const [worksheetFetchTrigger, setWorksheetFetchTrigger] = useState<string>('query');
    const viewMode = multidimensionalAllocationState.viewMode;

    // Execute and Clone belong to whoever holds the lock — see isBlockedByEditLock.
    const blockedByEditLock = isBlockedByEditLock(viewMode, editLockingEnabled);

    // A worksheet Pending Approval is awaiting an approval decision and must not be edited,
    // even by a user who holds no lock. Disable (grey out) the Edit button in that state
    // rather than letting it acquire the lock (mirrors the calculation Pending Review gate).
    const worksheetPendingApproval = selectedWorksheet?.status === 'PENDING_APPROVAL';

    const refetchWorksheet = () => setWorksheetFetchTrigger(`query-${Date.now()}`);

    const { handleEdit, handleSave, handleConfirm, handleRevert, actionInProgress, UnsavedChangesContextProvider } = useLockActions({
        componentType: LOCK_COMPONENT_TYPES.MDA_WORKSHEET,
        entityId: selectedWorksheet?.worksheetId,
        currentUser,
        dispatch: multidimensionalAllocationDispatch,
        setViewModeAction: ACTIONS.SET_VIEW_MODE,
        messageService: services.messageService,
        onRefetch: refetchWorksheet,
        messages: {
            editSuccess: 'You are now editing this worksheet.',
            lockedByOther: (lockedBy: string) => `This worksheet is locked by ${lockedBy}.`,
        },
    });

    // State for worksheet response data
    const [worksheetResponse, isLoading, error] =
        services.multidimensionalAllocationService.getMultiAllocationWorksheet(
            worksheetId,
            worksheetFetchTrigger,
            historicalWorksheetVersion
        );

    const lockInfo = selectedWorksheet?.lockInfo ?? worksheetResponse?.lockInfo;

    // Execute worksheet API call
    const [executeResponse, executeLoading, executeError] =
        services.multidimensionalAllocationService.executeMultiAllocationWorksheet(
            selectedWorksheet?.worksheetId,
            selectedWorksheet?.worksheetVersion,
            executeRequest
        );

    // Get execution status API call. Picking or clearing a past period never changes these arguments, so a return
    // to the current view neither fetches the current status again nor re-shows its error banner.
    const [currentStatusResponse, currentStatusLoading, currentStatusError] =
        services.multidimensionalAllocationService.getMultiAllocationExecutionStatus(
            selectedWorksheet?.worksheetId,
            undefined,
            retryRequest || 'query'
        );
    // The selected past execution's status; it fetches nothing outside the past view (no worksheet id).
    const [pastStatusResponse, pastStatusLoading, pastStatusError] =
        services.multidimensionalAllocationService.getMultiAllocationExecutionStatus(
            isViewingHistoricalData ? selectedWorksheet?.worksheetId : undefined,
            worksheetExecution?.executionId,
            `past#${resultLinkRefreshCount}`
        );
    // The header and the data sources show the selected execution's status in the past view.
    const executionStatusResponse = isViewingHistoricalData ? pastStatusResponse : currentStatusResponse;
    const executionStatusLoading = isViewingHistoricalData ? pastStatusLoading : currentStatusLoading;

    // Clone worksheet API call
    const [cloneResponse, cloneLoading, cloneError] =
        services.multidimensionalAllocationService.cloneWorksheet(cloneRequest);

    // Linked entities API call
    const [linkedEntitiesResponse, linkedEntitiesLoading, linkedEntitiesError] =
        services.multidimensionalAllocationService.getMultiAllocationLinkedEntities(
            EntityType.MULTI_ALLOCATION_WORKSHEET,
            selectedWorksheet?.worksheetId,
            linkedEntitiesLoadTrigger
        );

    // Execution group API calls
    const [executionGroupDetailsResult, isGettingExecutionGroupDetails, executionGroupDetailsError] =
        services.multidimensionalAllocationService.getOrAssignWorksheetExecutionGroup(
            selectedWorksheet?.worksheetId,
            loadTime
        );
    const [saveExecutionGroupResult, isSavingExecutionGroup, saveExecutionGroupError] =
        services.multidimensionalAllocationService.saveWorksheetExecutionGroup(saveExecutionGroupPayload);
    const [cliExecutionGroups, cliExecutionGroupsLoading, cliExecutionGroupsError] =
        services.calculationsService.getCLIExecutionGroups(loadTime);

    // Version selection logic: pick version -1 if exists, else version 0.
    // The data-source refetch + linked-entities re-trigger only fire on
    // user-driven refetches (Edit / Save / Confirm / Revert). On the initial
    // mount fetch the children already load on their own — dispatching the
    // refresh here would cause a redundant round of network calls on page
    // open.
    useEffect(() => {
        if (error) {
            services.messageService.showErrorBanner(error);
            return;
        }

        if (!worksheetResponse?.worksheets || worksheetResponse.worksheets.length === 0) {
            return;
        }

        multidimensionalAllocationDispatch(ACTIONS.SET_SELECTED_WORKSHEET.withPayload(worksheetResponse));
        if (worksheetFetchTrigger !== 'query') {
            multidimensionalAllocationDispatch(ACTIONS.SET_DATA_SOURCES_NEED_REFRESH.withPayload(true));
            setLinkedEntitiesLoadTrigger(String(Date.now()));
        }
        // Page open loads the execution group calls here, once the worksheet is known.
        setLoadTime(String(Date.now()));
    }, [worksheetResponse, error, multidimensionalAllocationDispatch]);

    // Edit-locking: load feature flag (once on mount)
    useEffect(() => {
        let cancelled = false;
        getFeatureFlags()
            .then((flags) => {
                if (!cancelled) {
                    setEditLockingEnabled(isFeatureEnabledForUser(flags, EDIT_LOCKING_FLAG, currentUser));
                }
            })
            .catch((err) => { console.error('Failed to load edit-locking feature flag', err); });
        return () => { cancelled = true; };
    }, []);

    // Edit-locking: derive viewMode from lockInfo when flag is on
    useEffect(() => {
        if (!editLockingEnabled || isLoading) return;
        multidimensionalAllocationDispatch(
            ACTIONS.SET_VIEW_MODE.withPayload(deriveViewModeFromLockInfo(lockInfo, currentUser))
        );
    }, [editLockingEnabled, lockInfo, currentUser, isLoading, multidimensionalAllocationDispatch]);

    // Trigger fetching linked entities when worksheet is loaded
    useEffect(() => {
        if (selectedWorksheet?.worksheetId) {
            setLinkedEntitiesLoadTrigger(new Date().toISOString());
        }
    }, [selectedWorksheet?.worksheetId]);

    // Handle linked entities error
    useEffect(() => {
        if (linkedEntitiesError) {
            services.messageService.showErrorBanner(`Failed to load linked entities: ${linkedEntitiesError}`);
        }
    }, [linkedEntitiesError, services.messageService]);

    // Handle save execution group response
    useEffect(() => {
        if (saveExecutionGroupResult == null) {
            return;
        }
        let successMsg = `Worksheet assigned to Group ${saveExecutionGroupResult.executionGroup} successfully.`;
        if (saveExecutionGroupResult.totalLinkedCalculations > 0) {
            successMsg += ` ${saveExecutionGroupResult.autoDraftsForActiveCalcsCreated || 0} active calculation(s) and `;
            successMsg += `${saveExecutionGroupResult.autoDraftsForPendingReviewCalcsCreated || 0} pending review calculation(s) `;
            successMsg += `linked to this worksheet were automatically moved to draft status due to this update.`;
        }
        services.messageService.showSuccessAutoDismissBanner(successMsg, 10000);
        // Update execution group details to trigger UI reset
        setLoadTime(new Date().toString());
    }, [saveExecutionGroupResult, services.messageService]);

    // Set dynamic breadcrumb + inject PromoteButton into the app-level h1 header actions
    useEffect(() => {
        const breadcrumbTitle = selectedWorksheet?.worksheetName
            ? `Multidimensional Allocation - ${selectedWorksheet.worksheetName}`
            : 'Multidimensional Allocation Calculation Manager';

        const headerActions = selectedWorksheet ? (
            <PromoteButton
                componentType={PromotionComponentType.MDA_WORKSHEET}
                componentId={selectedWorksheet.worksheetId}
                componentName={`MDA Worksheet ${selectedWorksheet.worksheetName}`}
                disabled={isViewingHistoricalData}
                disabledReason={
                    selectedWorksheet.status !== 'ACTIVE'
                        ? 'Only Active worksheets can be promoted.'
                        : undefined
                }
            />
        ) : undefined;

        globalDispatch(
            GLOBAL_ACTIONS.SET_DYNAMIC_BREADCRUMB.withPayload(
                new DynamicBreadcrumb(pathname, breadcrumbTitle, breadcrumbTitle, undefined, undefined, headerActions)
            )
        );
    }, [pathname, globalDispatch, selectedWorksheet, isViewingHistoricalData]);

    // Handle execute worksheet button click
    const handleExecuteWorksheet = () => {
        setIsDisableExecuteButton(true);
        setExecuteRequest(new Date().toISOString());
    };

    // Handle retry button click
    const handleRetry = () => {
        setRetryRequest(new Date().toISOString());
    };

    // Handle clone worksheet button click
    const handleCloneWorksheet = () => {
        setIsCloneModalVisible(true);
    };

    // Handle clone modal close
    const handleCloneModalClose = () => {
        setIsCloneModalVisible(false);
    };

    // Handle clone modal submit
    const handleCloneModalSubmit = (request: CloneWorksheetRequest) => {
        setCloneRequest(request);
    };

    // Handle execution group save
    const saveExecutionGroup = (executionGroup: string) => {
        setSaveExecutionGroupPayload({
            worksheetId: selectedWorksheet?.worksheetId,
            executionGroup: executionGroup,
        });
    };

    // Handle linked entities link click
    const handleLinkedEntitiesClick = () => {
        setIsLinkedEntitiesModalVisible(true);
    };

    // Handle linked entities modal close
    const handleLinkedEntitiesModalClose = () => {
        setIsLinkedEntitiesModalVisible(false);
    };

    // Get linked entities count
    const linkedEntitiesCount = linkedEntitiesResponse?.linkedEntities?.length || 0;

    // Handle execute worksheet response
    useEffect(() => {
        if (executeError) {
            services.messageService.showError(executeError);
            return;
        }

        if (executeResponse) {
            services.messageService.showSuccessBanner('Worksheet execution started successfully');
            setRetryRequest(new Date().toISOString());
        }
    }, [executeResponse, executeError, services.messageService]);

    // Handle execute worksheet error
    useEffect(() => {
        if (executeError) {
            services.messageService.showErrorBanner(`Failed to execute worksheet: ${executeError}`);
        }
    }, [executeError, services.messageService]);

    // Handle clone worksheet response
    useEffect(() => {
        if (cloneError) {
            services.messageService.showErrorBanner(cloneError);
            return;
        }

        if (cloneResponse && cloneResponse.worksheetData) {
            services.messageService.showSuccessBanner('Worksheet cloned successfully');
            setIsCloneModalVisible(false);
            setCloneRequest(null);

            // Navigate to the new cloned worksheet using soft redirect
            const newWorksheetId = cloneResponse.worksheetData.worksheetId;
            if (newWorksheetId) {
                history.push(`/calculations/multidimensionalAllocation/calculation/${newWorksheetId}`);
            }
        }
    }, [cloneResponse, cloneError, services.messageService, history]);

    // Handle clone worksheet error
    useEffect(() => {
        if (cloneError) {
            services.messageService.showErrorBanner(`Failed to clone worksheet: ${cloneError}`);
        }
    }, [cloneError, services.messageService]);

    useEffect(() => {
        const shouldDisable =
            !selectedWorksheet?.worksheetId ||
            executeLoading ||
            executionStatusLoading ||
            executionStatusResponse?.executionStatus === ExecutionStatus.IN_PROGRESS;
        setIsDisableExecuteButton(shouldDisable);
    }, [selectedWorksheet?.worksheetId, executeLoading, executionStatusResponse, executionStatusLoading]);

    // Handle retry response - refresh data sources if execution completed
    useEffect(() => {
        if (currentStatusError) {
            services.messageService.showErrorBanner(currentStatusError);
            return;
        }

        const currentError = executionErrorOf(currentStatusResponse);
        if (currentError) {
            services.messageService.showErrorBanner(currentError);
            return;
        }

        if (retryRequest) {
            multidimensionalAllocationDispatch(ACTIONS.SET_DATA_SOURCES_NEED_REFRESH.withPayload(true));
        }
    }, [currentStatusResponse, currentStatusError, retryRequest, multidimensionalAllocationDispatch]);

    // Each past-view data-source open fetches the status again, so each error shows once per selected execution.
    const pastViewErrors = useRef({ executionId: worksheetExecution?.executionId, shown: new Set<string>() });
    if (pastViewErrors.current.executionId !== worksheetExecution?.executionId) {
        pastViewErrors.current = { executionId: worksheetExecution?.executionId, shown: new Set<string>() };
    }
    useEffect(() => {
        const statusError = pastStatusError || executionErrorOf(pastStatusResponse);
        if (isViewingHistoricalData && statusError && !pastViewErrors.current.shown.has(statusError)) {
            pastViewErrors.current.shown.add(statusError);
            services.messageService.showErrorBanner(statusError);
        }
    }, [pastStatusResponse, pastStatusError]);

    // Show error if API call failed
    if (error && worksheetId) {
        return (
            <div className="multidimensionalAllocationBuilder">
                <SpaceBetween direction="vertical" size="l">
                    <Box>
                        <StatusIndicator type="error">Error loading worksheet: {error}</StatusIndicator>
                    </Box>
                </SpaceBetween>
            </div>
        );
    }

    return (
        <MultidimensionalAllocationContext.Provider
            value={{
                services,
                multidimensionalAllocationState,
                multidimensionalAllocationDispatch,
            }}>
          <UnsavedChangesContextProvider>
            <div className="multidimensionalAllocationBuilder">
                <SpaceBetween direction="vertical" size="l">
                    {/* Loading state */}
                    {isLoading && worksheetId && (
                        <Box>
                            <SpaceBetween direction="horizontal" size="s" alignItems="center">
                                <Spinner size="normal" />
                                <Box variant="span">Loading worksheet data...</Box>
                            </SpaceBetween>
                        </Box>
                    )}

                    {/* Edit-locking: banner when locked by another user */}
                    {editLockingEnabled && (
                        <LockBanner lockInfo={lockInfo} currentUser={currentUser} entityLabel="worksheet" />
                    )}

                    {viewMode === CONSTANTS.VIEW_MODE.EDITABLE && editLockingEnabled && (
                        <Box margin={{ bottom: 'm' }}>
                            <Alert type="warning">
                                Changes on this worksheet are not saved automatically. Click <b>Save</b> to persist your edits.
                            </Alert>
                        </Box>
                    )}

                    {/* Allocation sheet Status with Execute Button */}
                    <Box>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            {/* Left side - Status information */}
                            <ColumnLayout columns={4} borders="vertical">
                                <Box variant="awsui-key-label" padding={{ top: 'xxs' }}>
                                    Worksheet Status:{' '}
                                    {isLoading ? <StatusIndicator type="loading"/> : getStatusIndicator(selectedWorksheet?.status ?? 'DRAFT_IN_PROGRESS')}
                                </Box>
                                <Box variant="awsui-key-label" padding={{ top: 'xxs' }}>
                                    <Link variant="primary" onFollow={handleLinkedEntitiesClick}>
                                        {linkedEntitiesCount} Linked CLI(s)
                                    </Link>
                                </Box>
                                <Box variant="awsui-key-label" padding={{ top: 'xxs' }}>
                                    Last Execution Status:{' '}
                                    {(() => {
                                        const [label, statusType, color] = getExecutionStatusConfig(
                                            executionStatusResponse?.executionStatus || 'Not Started'
                                        );
                                        return (
                                            <StatusIndicator type={statusType} colorOverride={color}>
                                                {label}
                                            </StatusIndicator>
                                        );
                                    })()}
                                </Box>
                                <Box variant="awsui-key-label">
                                    {/* Execution History Dropdown */}
                                    <WorksheetPastExecutionResultsComponent
                                        worksheetId={selectedWorksheet?.worksheetId}
                                        worksheetName={selectedWorksheet?.worksheetName}
                                    />
                                </Box>
                            </ColumnLayout>

                            {/* Right side - Execute and Retry buttons */}
                            <SpaceBetween direction="horizontal" size="s">
                                <Button
                                    variant="link"
                                    iconName="refresh"
                                    disabled={executionStatusLoading || executeLoading || isViewingHistoricalData}
                                    onClick={handleRetry}></Button>
                                {canEdit && (
                                    <Button
                                        variant="primary"
                                        onClick={handleCloneWorksheet}
                                        disabled={isViewingHistoricalData || blockedByEditLock}
                                        disabledReason={
                                            isViewingHistoricalData
                                                ? 'Cannot clone worksheet when viewing historical data'
                                                : (blockedByEditLock
                                                    ? 'Click Edit to acquire the lock before cloning.'
                                                    : undefined)
                                        }>
                                        {'Clone Worksheet'}
                                    </Button>
                                )}
                                <Button
                                    variant="primary"
                                    loading={executeLoading}
                                    disabled={isDisableExecuteButton || isViewingHistoricalData || (canEdit && blockedByEditLock)}
                                    disabledReason={
                                        isViewingHistoricalData
                                            ? 'Cannot execute worksheet when viewing historical data'
                                            : (canEdit && blockedByEditLock
                                                ? 'Click Edit to acquire the lock before executing.'
                                                : undefined)
                                    }
                                    onClick={handleExecuteWorksheet}>
                                    {executeLoading ? 'Executing...' : 'Execute Worksheet'}
                                </Button>
                                {/* Edit-locking: lock lifecycle buttons */}
                                {canEdit && editLockingEnabled && !isViewingHistoricalData && (
                                    <EditLockButtonBar
                                        viewMode={viewMode}
                                        lockInfo={lockInfo}
                                        currentUser={currentUser}
                                        actionInProgress={actionInProgress}
                                        onEdit={handleEdit}
                                        onSave={handleSave}
                                        onConfirm={handleConfirm}
                                        onRevert={handleRevert}
                                        metadataLoaded={!isLoading && !!selectedWorksheet}
                                        editDisabled={worksheetPendingApproval}
                                        editDisabledReason="This worksheet is pending approval and cannot be edited."
                                    />
                                )}
                            </SpaceBetween>
                        </div>
                    </Box>
                    
                    {/* Execution Group Selection */}
                    <Container>
                        <TPELoadingSpinner loading={isGettingExecutionGroupDetails || isLoading}>
                            <WorksheetExecutionGroupSelector
                                cliExecutionGroups={cliExecutionGroups || []}
                                executionGroupDetails={executionGroupDetailsResult}
                                canEditAttributes={!isViewingHistoricalData && (!editLockingEnabled || viewMode === CONSTANTS.VIEW_MODE.EDITABLE)}
                                worksheet={selectedWorksheet}
                                saveExecutionGroup={saveExecutionGroup}
                                isSaving={isSavingExecutionGroup}
                            />
                        </TPELoadingSpinner>
                    </Container>

                    {/* Configure data sources */}
                    <div className='stepsWizard'>
                        <MultidimensionalAllocationDataSourcesContainer
                            worksheet={selectedWorksheet}
                            services={services}
                            containerTitle={'Configure Data Sources'}
                            executionGroupDetails={executionGroupDetailsResult}
                            executionStatusResponse={executionStatusResponse}
                            executionStatusLoading={executionStatusLoading}
                            refreshResultLinks={refreshResultLinks}
                            loading={isLoading}
                        />
                    </div>


                  
                </SpaceBetween>

                {/* Clone Worksheet Modal */}
                <CloneWorksheetModal
                    visible={isCloneModalVisible}
                    isCreating={cloneLoading}
                    createError={cloneError}
                    worksheetId={selectedWorksheet?.worksheetId}
                    worksheetVersion={selectedWorksheet?.worksheetVersion}
                    onClose={handleCloneModalClose}
                    onSubmit={handleCloneModalSubmit}
                />

                {/* Linked Entities Modal */}
                <LinkedEntitiesModal
                    visible={isLinkedEntitiesModalVisible}
                    entityName={selectedWorksheet?.worksheetName || ''}
                    entityType={EntityType.MULTI_ALLOCATION_WORKSHEET}
                    linkedEntities={linkedEntitiesResponse?.linkedEntities || []}
                    linkedCount={linkedEntitiesCount}
                    onDismiss={handleLinkedEntitiesModalClose}
                />
            </div>
          </UnsavedChangesContextProvider>
        </MultidimensionalAllocationContext.Provider>
    );
}

function getStatusIndicator(status: string) {
    const [label, statusType, color] = STATUS_INDICATOR_CONFIG[status as WorksheetStatus];
    return (
        <StatusIndicator type={statusType} colorOverride={color}>
            {label}
        </StatusIndicator>
    );
}
